import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useHoldHint } from './HoldHint.js'
import { useFinePointer } from '../net/useDevice.js'

/** How long an armed button stays armed before it forgets it was pressed. */
const ARM_MS = 4000

/**
 * The two-step press, in one place. Both controls in this file end up here on
 * some device or other, and the property they are both buying is the same one:
 * a single stray contact commits nothing, and a button left armed by a forgotten
 * press disarms itself rather than waiting to be triggered by a later fumble.
 */
function useArmed(onConfirm: () => void): { armed: boolean; press: () => void } {
  const [armed, setArmed] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )

  const press = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    if (armed) {
      setArmed(false)
      onConfirm()
      return
    }
    setArmed(true)
    timer.current = window.setTimeout(() => setArmed(false), ARM_MS)
  }, [armed, onConfirm])

  return { armed, press }
}

/**
 * Two-step confirmation for anything the engine will not undo. The second tap
 * has to be deliberate; the armed state disarms itself after a few seconds so a
 * forgotten button cannot be triggered by a later fumble.
 */
export function ConfirmButton({
  onConfirm,
  children,
  confirmLabel = 'Press again to confirm',
  className = '',
  disabled = false,
  testId,
  ariaLabel,
}: {
  onConfirm: () => void
  children: ReactNode
  confirmLabel?: string
  className?: string
  disabled?: boolean
  testId?: string
  /** Needed when the face of the button is a glyph, which names nothing. */
  ariaLabel?: string
}) {
  const { armed, press } = useArmed(onConfirm)

  return (
    <button
      type="button"
      className={`btn ${armed ? 'btn--armed' : ''} ${className}`}
      onClick={() => !disabled && press()}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      data-armed={armed ? 'true' : 'false'}
    >
      {armed ? confirmLabel : children}
    </button>
  )
}

/**
 * Long enough that a knuckle brushing the screen is not a vote, short enough
 * that it reads as a firm press rather than a wait. It used to be more than
 * twice this, which is a long time to stand still with a thumb down while nine
 * people watch you.
 */
const HOLD_MS = 300
/** Below this a press reads as a tap that slipped, not as an aborted hold. */
const TAP_MS = 60

/**
 * Commit an irreversible choice. Every one a player makes goes through this one
 * control - nominating, voting, enacting, using a power, executing - so the
 * mechanic has to be learnt exactly once, and a slip can never commit anything.
 *
 * What that mechanic *is* depends on what you are holding. On a phone it is a
 * press and hold: a thumb is imprecise, the screen is the only surface there
 * is, and filling a bar under your own finger is the clearest way to say "this
 * is happening, and you can still back out". On a laptop it is a two-step
 * click, because holding a mouse button down for a third of a second while a
 * bar fills is not a thing anything else on a computer asks of you - people let
 * go, nothing happens, and they conclude the button is broken. A click that
 * arms and a click that fires says the same thing in the idiom of the device.
 *
 * Both paths refuse a single contact, which is the only property the rest of
 * the app depends on.
 */
export function HoldToConfirm({
  onConfirm,
  children,
  className = '',
  disabled = false,
  testId,
  holdMs = HOLD_MS,
  oneClick = false,
}: {
  onConfirm: () => void
  children: ReactNode
  className?: string
  disabled?: boolean
  testId?: string
  holdMs?: number
  /**
   * On a mouse, commit on the first click instead of arming. Only the ballot
   * asks for this. Ja and Nein are two large buttons, side by side, that say
   * exactly what they do - there is no list to mis-aim at and nothing to read
   * first - and making the single most-pressed control in the game a
   * double-click taxed every round of every game to guard against a slip that
   * the shape of the control already makes unlikely. A thumb is a different
   * matter, so touch keeps the hold.
   */
  oneClick?: boolean
}) {
  const fine = useFinePointer()
  const [progress, setProgress] = useState(0)
  const start = useRef<number | null>(null)
  const frame = useRef<number | null>(null)
  const hint = useHoldHint()
  const { armed, press } = useArmed(onConfirm)

  // Registering on mount is what lets the hint fire after ten idle seconds:
  // the provider only counts, it never needs to know what the buttons do. On a
  // mouse there is no hold on screen to explain, so nothing registers and the
  // hint never comes up.
  const register = hint?.register
  useEffect(() => {
    if (fine) return
    return register?.()
  }, [register, fine])

  const stop = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = null
    start.current = null
    setProgress(0)
  }, [])

  /** Released by hand. An early release is the moment to explain the control. */
  const release = useCallback(() => {
    const began = start.current
    stop()
    if (began !== null && performance.now() - began > TAP_MS) hint?.nudge()
  }, [stop, hint])

  const step = useCallback(() => {
    if (start.current === null) return
    const elapsed = performance.now() - start.current
    const pct = Math.min(1, elapsed / holdMs)
    setProgress(pct)
    if (pct >= 1) {
      stop()
      onConfirm()
      return
    }
    frame.current = requestAnimationFrame(step)
  }, [onConfirm, stop, holdMs])

  useEffect(() => stop, [stop])

  if (fine) {
    return (
      <button
        type="button"
        className={`btn btn--hold ${armed ? 'btn--armed' : ''} ${className}`}
        disabled={disabled}
        data-testid={testId}
        data-armed={armed ? 'true' : 'false'}
        data-holding="false"
        onClick={() => {
          if (disabled) return
          if (oneClick) onConfirm()
          else press()
        }}
      >
        {/*
          The armed face keeps the name it had. A row of choices all reading
          "click again to confirm" tells you that something is armed but not
          which of nine people you are about to shoot.
        */}
        <span className="btn__text">
          {children}
          {armed && (
            <span className="btn__again">
              <span className="btn__again-sep"> — </span>click again
            </span>
          )}
        </span>
      </button>
    )
  }

  return (
    <button
      type="button"
      className={`btn btn--hold ${className}`}
      disabled={disabled}
      data-testid={testId}
      data-holding={progress > 0 ? 'true' : 'false'}
      onPointerDown={() => {
        if (disabled) return
        start.current = performance.now()
        frame.current = requestAnimationFrame(step)
      }}
      onPointerUp={release}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onKeyDown={(e) => {
        // Keyboard users get a plain activation rather than a timed hold.
        if (e.key === 'Enter') {
          e.preventDefault()
          onConfirm()
        }
      }}
    >
      <span className="btn__fill" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />
      <span className="btn__text">{children}</span>
    </button>
  )
}

/**
 * The line under a set of choices that says how to commit one. It has to agree
 * with whichever control HoldToConfirm actually rendered, so it reads the same
 * signal rather than hard-coding the word "hold" the way it used to - on a
 * laptop that sentence described a mechanic that was not on the screen.
 */
export function ConfirmHint({
  subject,
  oneClick = false,
}: {
  subject: string
  /** Matches the `oneClick` on the buttons this line is describing. */
  oneClick?: boolean
}) {
  const fine = useFinePointer()
  if (!fine) return <>Hold a {subject} until it fills.</>
  if (oneClick) return <>One click casts it.</>
  return <>Click a {subject}, then click it again to confirm.</>
}
